import React, { useState } from "react";
import styles from "./pal.module.css";
import axios from "axios";
import NewMessageModal from "../newMessageModal/NewMessageModal";
import RemoveConfirmationModal from "./removeConfirmationModal/RemoveConfirmationModal";
import { MdPersonRemoveAlt1 } from "react-icons/md";
import { FaRegEnvelope } from "react-icons/fa6";

const apiUrl = process.env.REACT_APP_URL;

const Pal = ({
  currUsername,
  isOwnProfile,
  username,
  imgUrl,
  removePalFromList,
}) => {
  /* The Pal component displays a single Pal (friend) of a user. It is used in the Pals page. The username and profile image of the Pal are shown. The active user can send a text / voice message to the Pal, and visit the profile page of the Pal. If the active user is the owner of this list (rather than a Pal of the owner, which may also view the owner's Pals list), he / she can also remove a Pal from the list. */
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRemoveBtnText, setShowRemoveBtnText] = useState(false);
  const [showMessageBtnText, setShowMessageBtnText] = useState(false);
  /**
   * Generate a random file name. This is used for audio message recordings
   * @param {*} extension - the requested file extension
   * @returns string, which consists of a the generated random string, and the passed extension.
   */
  const generateRandomFilename = (extension) => {
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${randomString}.${extension}`;
  };

  /**
   * Open the profile page of the Pal in a new tab
   */
  const visitProfilePage = () => {
    window.open("/profile/" + username, "_blank");
  };

  /**
   * Show the remove Pal confirmation modal, for the user to confirm the Pal removal from the Pals list.
   */
  const handleRemoveBtnClick = (e) => {
    e.stopPropagation();
    setShowRemoveModal(true);
  };

  /**
   * Remove the Pal from the user's Pals list (cancel the friendship between them).
   */
  const handleConfirmRemove = async () => {
    try {
      const res = await axios.put(
        `${apiUrl}/relationships/remove/${currUsername}/${username}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200) {
        removePalFromList(username);
      } else {
        console.error("Error - the Pal was not removed.");
      }
    } catch (error) {
      console.error("Error trying to remove Pal", error);
    }
    setShowRemoveModal(false);
  };

  /**
   * Show the new message modal to allow the user to send a message to the Pal.
   */
  const handleMessageBtnClick = (e) => {
    e.stopPropagation();
    setShowMessageModal(true);
  };

  /**
   * Close the new message modal
   */
  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
  };

  /**
   * Send a message to the Pal.
   * @param {*} messageData object with properties: type ("text" / "audio"), content, receiverUsername (the Pal's username)
   */
  const handleSendMessage = async (messageData) => {
    const { type, content, receiverUsername } = messageData;

    if (!content || !receiverUsername) return;

    if (type === "text") {
      try {
        const response = await axios.post(
          `${apiUrl}/messages/new/text`,
          {
            senderUsername: currUsername,
            receiverUsername,
            textContent: content,
          },
          {
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send text message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending text message:", error);
      }
    } else if (type === "audio") {
      try {
        const formData = new FormData();
        const randomFilename = generateRandomFilename("webm");
        formData.append("audio", content, randomFilename);
        formData.append("senderUsername", currUsername);
        formData.append("receiverUsername", receiverUsername);

        const response = await axios.post(
          `${apiUrl}/messages/new/voice`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send voice message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending voice message:", error);
      }
    }
  };

  /**
   * Close the remove Pal confirmation modal
   */
  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
  };

  return (
    <div className={styles.pal}>
      <div onClick={visitProfilePage}>
        <figure>
          <img src={`${apiUrl}/${imgUrl}`} alt={username} />
          <figcaption>{username}</figcaption>
          <div className={styles.btnsContainer}>
            {isOwnProfile && (
              <button
                className={styles.removeBtn}
                onMouseEnter={() => setShowRemoveBtnText(true)}
                onMouseLeave={() => setShowRemoveBtnText(false)}
                onClick={handleRemoveBtnClick}
              >
                <MdPersonRemoveAlt1 />
              </button>
            )}
            {isOwnProfile && showRemoveBtnText && (
              <span className={styles.removePalText}>Remove</span>
            )}
            <button
              className={styles.msgBtn}
              onMouseEnter={() => setShowMessageBtnText(true)}
              onMouseLeave={() => setShowMessageBtnText(false)}
              onClick={handleMessageBtnClick}
            >
              <FaRegEnvelope />
            </button>
            {showMessageBtnText && (
              <span className={styles.msgPalText}>Message</span>
            )}
          </div>
        </figure>
      </div>

      {showRemoveModal && (
        <RemoveConfirmationModal
          show={showRemoveModal}
          onClose={handleCloseRemoveModal}
          onConfirm={handleConfirmRemove}
        />
      )}

      {showMessageModal && (
        <NewMessageModal
          show={showMessageModal}
          onClose={handleCloseMessageModal}
          onSendMessage={handleSendMessage}
          receiverUsername={username}
        />
      )}
    </div>
  );
};

export default Pal;
